import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatMoney } from "@/lib/money";

export interface TipReceiptDetailsData {
  id: string;
  status: string;
  amountMinor: number;
  currency: string;
  provider: string | null;
  reference: string | null;
  createdAt: string | null;
  completedAt: string | null;
  refundedAt: string | null;
  writerName: string | null;
  publicationId: string | null;
  publicationTitle: string | null;
  reason: string | null;
  history: Array<{
    event: string;
    outcome: string;
    at: string;
    reason: string | null;
    source: string;
  }>;
}

function stamp(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/** Plain-English explanation of where a tip ended up and why. */
export function explainTipStatus(status: string, reason: string | null): string {
  const because = reason ? ` Reason given: ${reason}.` : "";
  switch (status) {
    case "pending":
      return "Checkout has started but the payment provider hasn't confirmed the charge yet. This usually settles within a minute.";
    case "completed":
      return `The charge was confirmed and the writer's balance has been credited, less the platform fee.${because}`;
    case "refunded":
      return `The full amount has been returned to your original payment method. Depending on your bank it can take a few working days to appear.${because}`;
    case "disputed":
      return `Your bank has raised a chargeback against this payment. The money is held while the payment provider reviews it, and the writer has been told the tip is on hold.${because}`;
    case "failed":
      return `The payment could not be collected, so nothing was charged and nothing was paid out. You're welcome to try again.${because}`;
    default:
      return "This tip is in an unusual state. Contact support if it doesn't resolve itself.";
  }
}

function describeEvent(event: string) {
  const map: Record<string, string> = {
    "payment.completed": "Payment confirmed",
    "payment.succeeded": "Payment confirmed",
    "payment.refunded": "Refund issued",
    "payment.disputed": "Chargeback opened",
    "payment.failed": "Payment failed",
  };
  return map[event] ?? event;
}

/**
 * The full story behind a tip: every status change, the reason recorded for
 * it, and the links a supporter needs to follow it up — the publication it
 * was for and the payment reference to quote to their bank or to support.
 */
export function TipReceiptDetails({
  open,
  onOpenChange,
  tip,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tip: TipReceiptDetailsData;
}) {
  const [downloading, setDownloading] = React.useState(false);

  async function downloadReceipt() {
    setDownloading(true);
    try {
      const { downloadTipReceiptPdf } = await import("@/lib/payments/receipt-pdf");
      await downloadTipReceiptPdf(tip, {
        explanation: explainTipStatus(tip.status, tip.reason),
        siteOrigin: window.location.origin,
      });
    } catch (error) {
      toast.error("Could not build the receipt", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Tip receipt</DialogTitle>
          <DialogDescription>
            {formatMoney(tip.amountMinor, tip.currency)}
            {tip.writerName ? ` to ${tip.writerName}` : ""} — {tip.status}
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{explainTipStatus(tip.status, tip.reason)}</p>

        <dl className="mt-2 space-y-2 rounded-xl border border-border/60 bg-surface/50 p-4 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Started</dt>
            <dd className="text-foreground">{stamp(tip.createdAt) ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Confirmed</dt>
            <dd className="text-foreground">{stamp(tip.completedAt) ?? "—"}</dd>
          </div>
          {tip.refundedAt ? (
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">
                {tip.status === "disputed" ? "Disputed" : "Refunded"}
              </dt>
              <dd className="text-foreground">{stamp(tip.refundedAt)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Payment reference</dt>
            <dd className="max-w-[60%] break-all text-right font-mono text-xs text-foreground">
              {tip.reference ?? "Not issued"}
            </dd>
          </div>
          {tip.provider ? (
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Processed by</dt>
              <dd className="text-foreground capitalize">{tip.provider}</dd>
            </div>
          ) : null}
        </dl>

        {tip.history.length > 0 ? (
          <div className="mt-2">
            <p className="text-sm font-medium text-foreground">Status changes</p>
            <ol className="mt-2 space-y-2">
              {tip.history.map((entry, index) => (
                <li
                  key={`${entry.event}-${entry.at}-${index}`}
                  className="rounded-lg border border-border/50 p-3 text-sm"
                >
                  <span className="block text-foreground">{describeEvent(entry.event)}</span>
                  <span className="block text-xs text-muted-foreground">
                    {stamp(entry.at)} · {entry.source === "admin" ? "recorded by staff" : "payment provider"}
                    {entry.outcome && entry.outcome !== "applied" ? ` · ${entry.outcome}` : ""}
                  </span>
                  {entry.reason ? (
                    <span className="mt-1 block text-xs text-muted-foreground">{entry.reason}</span>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        <div className="mt-2 flex flex-wrap gap-2">
          {tip.publicationId ? (
            <Button asChild size="sm" variant="soft">
              <Link to="/story/$storyId" params={{ storyId: tip.publicationId }}>
                <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                {tip.publicationTitle ?? "View the publication"}
              </Link>
            </Button>
          ) : null}
          <Button size="sm" variant="soft" onClick={downloadReceipt} disabled={downloading}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            {downloading ? "Preparing…" : "Download receipt"}
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link to="/help">Get help with this payment</Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
